// Отметка «выплачено» по расчёту человека.
//
// Сценарии приёмки 37 и 76: «нажатие «выплачено» фиксирует дату, автора, способ и приложенное
// подтверждение перевода». Дверь на сервере была и закрыта тестами, а нажать было негде: в
// программе факт выплаты не отмечался ниоткуда, и колонка «Реквизиты выплаты» стояла пустой у
// всех до единого.
//
// ## Сумма не подставляется молча
//
// Так устроена и серверная форма, и это правильно: переводят иногда не всё сразу, и
// подставленная сумма превращала бы частичную выплату в полную одним нажатием. Поле
// предзаполнено итогом, но оно ПОЛЕ, а не подпись: человек видит число и правит его.
//
// ## Уже отмеченную выплату этот блок не правит
//
// Правки денежной записи не существует нигде в продукте: ошиблись — сторно встречной записью.
// Поэтому здесь после отметки остаётся только то, что произошло, и кнопки «изменить» нет.
//
// ## Доплатить разницу
//
// Отметок по одному расчёту бывает несколько: переводят не всё сразу, а после пересчёта
// ведомости итог может вырасти. Сколько человеку ещё должны, видно только по сумме ВСЕХ
// действующих отметок (`paid_total`). Сумма последней (`paid_amount`) для этого не годится: на
// каждой частичной выплате она врёт. До 01.10.2026 форма отметки появлялась только когда
// отметки нет вовсе, и доплатить было нечем: оставалось сторнировать прежнюю отметку и
// проставить всю сумму заново.
//
// Доплата — ТА ЖЕ форма, что и первая отметка (`ФормаОтметки`): подтверждение перевода,
// проверки суммы и ключ операции общие, второй набор правил про деньги рядом означал бы второй
// набор ошибок. Отличаются две вещи: что подставлено в поле суммы (разница, а не итог) и что
// написано на кнопке.
//
// Расхождение итога с суммой отметок, которое нельзя доплатить, называется словами и действий
// не несёт, без кнопок и без совета. Таких случаев два. Переведено больше итога: система сама
// ничего не удерживает и не сторнирует, решать человеку. Ведомость выплачена целиком или
// закрыта: доплату по ней не предлагаем, но недоплату называем, иначе она остаётся видна только
// тому, кто сам сложит числа.

import { useRef, useState } from 'react';

import { RiCheckboxCircleLine, RiErrorWarningLine, RiInformationLine } from '@remixicon/react';

import type { PayoutMethod } from '@/shared/api/endpoints/payouts';
import type { PayrollEmployeeOut } from '@/shared/api/endpoints/payroll';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { uploadFile } from '@/shared/api/endpoints/accountable';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { parseRublesToKopecks } from '@/shared/money/parse';
import { formatDateOnly } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { FilePicker } from '@/shared/ui/file-picker';
import { TextField } from '@/shared/ui/text-field';

import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';

import { useMarkPaid, useReverseFact } from '../../queries';

/** Сторно отметки — право руководства и администратора: отмечает выплату финотдел, отменяет другой. */
const ПРАВО_СТОРНО = 'core.payout.reverse';

/** Способы перевода из контракта: их ровно два, и оба из бланков заказчика. */
const СПОСОБЫ: Readonly<Record<string, string>> = {
  card: 'на карту',
  sbp: 'через систему быстрых платежей',
};

/**
 * Кому идти, когда отметить выплату нельзя. Текст один на обе кнопки, «Выплачено» и «Доплатить
 * разницу»: две редакции одного объяснения однажды назвали бы двух разных адресатов.
 */
const ТЕКСТ_НЕТ_ПРАВА =
  'Отмечает выплату финансовый отдел или администратор. Скопируйте ссылку на эту ведомость и передайте им.';

/**
 * Есть ли что доплатить: отметка есть, итог больше суммы ВСЕХ действующих отметок, и ведомость ещё
 * не выплачена целиком. Панель человека по этому признаку решает, где стоит блок выплаты: когда
 * доплатить есть что, это главное действие панели, и редкая форма «Проставить сумму по колонке» не
 * должна стоять между человеком и ним.
 */
export function естьДоплата(человек: PayrollEmployeeOut, ведомостьВыплачена: boolean): boolean {
  return (
    человек.paid_at !== null && человек.total_payout - человек.paid_total > 0 && !ведомостьВыплачена
  );
}

export function PaidBlock({
  человек,
  можноПравить,
  ведомостьВыплачена = false,
}: {
  человек: PayrollEmployeeOut;
  можноПравить: boolean;
  /** Ведомость выплачена целиком либо закрыта: доплату по ней не предлагаем. */
  ведомостьВыплачена?: boolean;
}) {
  const сторно = useReverseFact();
  const права = useAuthStore((состояние) => состояние.permissions);
  const [основание, setОснование] = useState('');
  const [отменяем, setОтменяем] = useState(false);

  const реквизиты = реквизитыСтрокой(человек);

  if (человек.paid_at !== null) {
    const можноСторно = can(права, ПРАВО_СТОРНО) && человек.payout_fact_id !== null;
    // Сколько человеку ещё должны: итог минус ВСЕ действующие отметки, а не последняя.
    const осталось = человек.total_payout - человек.paid_total;
    // Пометка без кнопок: называет расхождение и ничего не предлагает и не советует. Совет тут
    // легко окажется неверным: что делать с переплатой или с недоплатой в уже выплаченной
    // ведомости, решает человек, а не экран. Молчать при этом нельзя: итог и сумма отметок
    // расходятся, и по одной строке «Выплачено» этого не видно.
    const пометка =
      осталось < 0
        ? `Переведено на ${formatRubles(-осталось)} больше, чем положено по расчёту. Система сама ничего не удерживает: решите, как это учесть.`
        : осталось > 0 && ведомостьВыплачена
          ? `Ведомость уже выплачена, но переведено на ${formatRubles(осталось)} меньше итога по расчёту.`
          : null;
    return (
      <div className='flex flex-col gap-2'>
        <span className='text-label-sm text-text-strong-950'>Выплата</span>
        <Alert.Root variant='light' status='success' size='small' role='status'>
          <Alert.Icon as={RiCheckboxCircleLine} />
          <span>
            Выплачено {formatDateOnly(человек.paid_at)}
            {человек.paid_amount !== null && <> на {formatRubles(человек.paid_amount)}</>}
            {реквизиты !== null && <> · {реквизиты}</>}.
          </span>
        </Alert.Root>

        {осталось > 0 && !ведомостьВыплачена && (
          <ДоплатаРазницы человек={человек} осталось={осталось} можноПравить={можноПравить} />
        )}
        {пометка !== null && (
          <Alert.Root variant='lighter' status='information' size='small' role='status'>
            <Alert.Icon as={RiInformationLine} />
            <span>{пометка}</span>
          </Alert.Root>
        )}

        <p className='text-paragraph-xs text-text-sub-600'>
          Отметку не правят: денежная запись меняется только встречной. Ошиблись — оформите сторно,
          а не правку.
        </p>

        {/* Кнопка стоит рядом с этой фразой не для красоты. До 06.09.2026 фраза была, а
            операции не существовало: отменить отметку было нечем ничем, и человек, которому
            перевод отменили, навсегда пропадал из списка «кому перевести». */}
        {!можноСторно ? (
          <p className='text-paragraph-xs text-text-sub-600'>
            Сторно оформляют руководство и администратор: отмечает выплату финансовый отдел,
            отменяет другой человек.
          </p>
        ) : !отменяем ? (
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='small'
            onClick={() => setОтменяем(true)}
          >
            Оформить сторно
          </Button.Root>
        ) : (
          <div className='flex flex-col gap-2'>
            <TextField
              id='paid-reversal-basis'
              label='Почему отменяем'
              required
              value={основание}
              onChange={setОснование}
              placeholder='Перевод не ушёл, ушёл не тому, сумма не та'
            />
            {сторно.error !== null && <ErrorAlert error={сторно.error} />}
            <div className='flex gap-2'>
              <Button.Root
                variant='error'
                mode='stroke'
                size='small'
                disabled={основание.trim() === '' || сторно.isPending}
                onClick={() => {
                  if (человек.payout_fact_id === null || основание.trim() === '') {
                    return;
                  }
                  сторно.mutate(
                    { factId: человек.payout_fact_id, basis: основание.trim() },
                    {
                      onSuccess: () => {
                        setОтменяем(false);
                        setОснование('');
                      },
                    },
                  );
                }}
              >
                {сторно.isPending ? 'Отменяем…' : 'Отменить выплату'}
              </Button.Root>
              <Button.Root
                variant='neutral'
                mode='ghost'
                size='small'
                onClick={() => {
                  setОтменяем(false);
                  setОснование('');
                }}
              >
                Не отменять
              </Button.Root>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <span className='text-label-sm text-text-strong-950'>Выплата</span>
      <ФормаОтметки
        человек={человек}
        можноПравить={можноПравить}
        суммаПоУмолчанию={человек.total_payout}
        подпись='Выплачено'
        подсказка='Подставлен итог к выплате. Перевели не всё: впишите сумму, частичная выплата тоже отмечается.'
      />
    </div>
  );
}

/**
 * Предложение доплатить: сколько осталось, кнопка и под ней та же форма отметки.
 *
 * Форма раскрывается по нажатию и закрывается сама после успешной отметки. Без права форма не
 * раскрывается вовсе: заполнять нечего, если отправить всё равно нельзя, и объяснение приходит
 * с первого нажатия, а не со второго.
 *
 * Отмены у формы нет намеренно. Ключ операции живёт, пока жива форма, и меняется только после
 * успеха: закрыть форму после обрыва связи и открыть заново значило бы потерять ключ, то есть
 * превратить повтор в настоящую вторую выплату. Нажимать «Доплатить» не обязательно: раскрытая
 * форма ничего не отправляет.
 */
function ДоплатаРазницы({
  человек,
  осталось,
  можноПравить,
}: {
  человек: PayrollEmployeeOut;
  /** Сколько ещё должны человеку, копейки. Больше нуля: иначе блок не рисуется. */
  осталось: number;
  можноПравить: boolean;
}) {
  const [раскрыта, setРаскрыта] = useState(false);
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <div className='flex flex-col gap-2'>
      <Alert.Root variant='light' status='warning' size='small' role='status'>
        <Alert.Icon as={RiErrorWarningLine} />
        <span>Итог больше, чем переведено: осталось доплатить {formatRubles(осталось)}.</span>
      </Alert.Root>

      {раскрыта ? (
        <ФормаОтметки
          человек={человек}
          можноПравить={можноПравить}
          суммаПоУмолчанию={осталось}
          подпись='Доплатить'
          подсказка='Подставлено то, что осталось доплатить. Перевели другую сумму: впишите её.'
          onГотово={() => setРаскрыта(false)}
        />
      ) : (
        <>
          {/* Главное действие панели, пока есть что доплатить: заливка, а не рамка. Рамкой стояла
              вторичная кнопка под предупреждением, и первым в панели читалась форма, которой
              пользуются редко (замечание независимой проверки 01.10.2026). */}
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xsmall'
            className='self-start'
            onClick={() => {
              if (!можноПравить) {
                // Кнопка не сереет, а объясняет: выключенная не доводит нажатие до обработчика.
                setЗапрет(ТЕКСТ_НЕТ_ПРАВА);
                return;
              }
              setЗапрет(null);
              setРаскрыта(true);
            }}
          >
            Доплатить разницу
          </Button.Root>
          {запрет !== null && (
            <Hint.Root hasError>
              <Hint.Icon as={RiErrorWarningLine} />
              {запрет}
            </Hint.Root>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Форма отметки выплаты: реквизиты, подтверждение перевода, сумма и кнопка.
 *
 * Одна на два места, первую отметку и доплату. Всё, что уходит на сервер, собирается здесь и
 * больше нигде: проверки суммы, снимок до отметки, ключ операции и засов от второй отправки.
 * Места различаются только тем, что подставлено в сумму, подсказкой под полем и надписью на
 * кнопке, и эти три вещи приходят параметрами.
 */
function ФормаОтметки({
  человек,
  можноПравить,
  суммаПоУмолчанию,
  подпись,
  подсказка,
  onГотово,
}: {
  человек: PayrollEmployeeOut;
  можноПравить: boolean;
  /** Что предложить в поле суммы, копейки: итог при первой отметке, разница при доплате. */
  суммаПоУмолчанию: number;
  /** Надпись на кнопке в покое. */
  подпись: string;
  /** Подсказка под полем суммы. */
  подсказка: string;
  /** Что сделать после успешной отметки. Первая отметка ничего: блок перерисуется данными. */
  onГотово?: () => void;
}) {
  const отметка = useMarkPaid();
  const [сумма, setСумма] = useState(() => formatKopecks(суммаПоУмолчанию));
  // День перевода. По умолчанию сегодня, но деньги уходят и раньше отметки: 11.10.2026
  // заказчик перевёл два объекта вне программы и отмечал позже — без этого поля в реквизитах
  // выплаты напечатался бы день нажатия.
  const [день, setДень] = useState(todayIso);
  const [ошибкаДня, setОшибкаДня] = useState<string | undefined>(undefined);
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [подтверждение, setПодтверждение] = useState<File | null>(null);
  const [грузим, setГрузим] = useState(false);
  // Ключ операции живёт столько же, сколько форма, и меняется только после успеха. Повтор
  // того же нажатия — обрыв связи, второе нажатие, возврат из офлайна — приходит на сервер с
  // тем же ключом и возвращает ПРЕЖНЮЮ отметку, а не заводит вторую.
  const [ключ, setКлюч] = useState(newIdempotencyKey);
  // Засов от второй отправки. Ref, а не состояние: состояние обновляется к следующей
  // отрисовке, а между двумя нажатиями подряд её не происходит — оба читают «не идёт», оба
  // грузят снимок и оба заводят денежную запись. Ключ операции спасает от второй ЗАПИСИ, но
  // не от второй загрузки файла и не от двух запросов подряд.
  const идёт = useRef(false);

  const реквизиты = реквизитыСтрокой(человек);

  return (
    <div className='flex flex-col gap-2'>
      {реквизиты === null ? (
        // «Реквизитов нет» и «выплата не отмечена» — разные факты, и первый мешает второму.
        <p className='text-paragraph-sm text-text-sub-600'>
          Заявления о реквизитах у человека нет: платить некуда, и в печатной форме клетка
          реквизитов останется пустой. Заявление заводится в его карточке, вкладка «Выплаты».
        </p>
      ) : (
        <p className='text-paragraph-sm text-text-sub-600'>Реквизиты: {реквизиты}</p>
      )}

      {/* Подтверждение перевода. Сценарий 76 требует его прямо: «поле для приложения
          подтверждения перевода (изображение)». Заказчик платит с карты на карту между
          физлицами — кассового документа там нет, и снимок перевода это ЕДИНСТВЕННОЕ
          доказательство того, что деньги ушли. Поле необязательное: отметить выплату без
          снимка можно, но это осознанный выбор, а не пропуск. */}
      <FilePicker
        id='paid-proof'
        label='Подтверждение перевода'
        подсказка='Выбрать снимок перевода с диска'
        file={подтверждение}
        onPick={setПодтверждение}
        disabled={грузим || отметка.isPending}
      />

      <TextField
        id='paid-amount'
        label='Сколько перевели'
        required
        value={сумма}
        onChange={(значение) => {
          setСумма(значение);
          setОшибка(undefined);
        }}
        placeholder={formatKopecks(суммаПоУмолчанию)}
        hint={подсказка}
        error={ошибка}
      />

      <DateField
        id='paid-on'
        label='Когда перевели'
        value={день}
        onChange={(значение) => {
          setДень(значение);
          setОшибкаДня(undefined);
        }}
        hint='Если деньги ушли раньше, поставьте их день: он встанет в отметку и в реквизиты выплаты.'
        error={ошибкаДня}
      />

      {отметка.isError && <ErrorAlert error={отметка.error as never} />}
      {запрет !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {запрет}
        </Hint.Root>
      )}

      <Button.Root
        type='button'
        variant='primary'
        mode='filled'
        size='xsmall'
        className='self-start'
        onClick={() => {
          void отметить();
        }}
      >
        {грузим ? 'Грузим снимок…' : отметка.isPending ? 'Отмечаем…' : подпись}
      </Button.Root>
    </div>
  );

  async function отметить() {
    if (!можноПравить) {
      // Кнопка не сереет, а объясняет: выключенная не доводит нажатие до обработчика.
      setЗапрет(ТЕКСТ_НЕТ_ПРАВА);
      return;
    }
    const разобранная = parseRublesToKopecks(сумма);
    if (!разобранная.ok) {
      setОшибка('Сумма пишется числом: 33 131 или 33 131,50.');
      return;
    }
    if (разобранная.value <= 0) {
      setОшибка('Ноль не выплата: отмечать нечего.');
      return;
    }
    if (день === '') {
      setОшибкаДня('Поставьте день перевода: дд.мм.гггг.');
      return;
    }
    if (идёт.current || грузим || отметка.isPending) {
      setЗапрет('Выплата уже отмечается, ждём ответ сервера. Второй раз не отправляем.');
      return;
    }
    setЗапрет(null);
    идёт.current = true;

    // Снимок грузится ДО отметки и отдельным запросом: при обрыве теряется загрузка, а не
    // денежная запись. Не загрузился — отметку не ставим вовсе: «выплачено» без обещанного
    // подтверждения хуже, чем неотмеченная выплата, потому что вернуться к ней уже незачем.
    let подтверждениеId: string | null = null;
    if (подтверждение !== null) {
      setГрузим(true);
      try {
        подтверждениеId = (await uploadFile(подтверждение, 'payment_proof')).id;
      } catch {
        setГрузим(false);
        идёт.current = false;
        setЗапрет(
          'Снимок перевода не загрузился. Выплату не отметили: попробуйте ещё раз либо уберите снимок и отметьте без него.',
        );
        return;
      }
      setГрузим(false);
    }

    отметка.mutate(
      {
        payroll_employee_id: человек.id,
        amount: разобранная.value,
        // Способ берётся из действующего заявления: платят тем, что человек в нём указал.
        // Строкой мимо перечня сервер его не примет, и это правильно — «перевод» и
        // «переводом» превратились бы в два способа с одним смыслом.
        method_used: способПеревода(человек.payout_method),
        proof_file_id: подтверждениеId,
        paid_on: день,
        idempotencyKey: ключ,
      },
      {
        // Новый ключ — только после успеха: следующая отметка по этому же человеку это уже
        // другая выплата (перевели остаток), и связывать её с прежней ключом нельзя.
        onSuccess: () => {
          setКлюч(newIdempotencyKey());
          onГотово?.();
        },
        onSettled: () => {
          идёт.current = false;
        },
      },
    );
  }
}

/** Реквизиты действующего заявления одной строкой: «•••• 4417 · на карту». Нет заявления — пусто. */
function реквизитыСтрокой(человек: PayrollEmployeeOut): string | null {
  return человек.payout_last4 === null
    ? null
    : `•••• ${человек.payout_last4}${
        человек.payout_method === null
          ? ''
          : ` · ${СПОСОБЫ[человек.payout_method] ?? человек.payout_method}`
      }`;
}

/** Способ из заявления, если он из известного перечня. Чужое слово наружу не уходит. */
function способПеревода(значение: string | null): PayoutMethod | null {
  return значение !== null && значение in СПОСОБЫ ? (значение as PayoutMethod) : null;
}
